// src/services/lesson.service.js
import { db, auth } from "@/firebase";
import {
  doc,
  collection,
  addDoc,
  getDocs,
  updateDoc,
  query,
  where,
  getDoc,
  orderBy,
  limit,
  Timestamp,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { functions } from "@/firebase";
import { Logger } from "@/utils/logger";
import { FeatureFlags } from "@/config/features";
import {
  NotificationService,
  NOTIFICATION_TYPES,
} from "@/services/notification.service";
import ReminderService from "@/services/reminder.service";
import { awardBadgesFromReview } from "@/services/badge.service";
import {
  COLLECTIONS,
  REVIEW_STATUS,
  validateReview,
} from "@/services/database.schema";

// Lesson status constants
export const LESSON_STATUSES = {
  // Pre-payment checkout states (see createLessonRecord / stripeWebhook). A
  // booking starts life as PENDING_PAYMENT and only becomes REQUESTED once the
  // payment succeeds; a failed payment lands on PAYMENT_FAILED.
  PENDING_PAYMENT: "pending_payment",
  PAYMENT_FAILED: "payment_failed",
  REQUESTED: "requested",
  CONFIRMED: "confirmed",
  REJECTED: "rejected",
  CANCELLATION_REQUESTED: "cancel-requested",
  CANCELLED: "cancelled",
  COMPLETED: "completed",
  REVIEWED: "reviewed",
};

// Cloud Function references
const confirmLessonCloudFunction = httpsCallable(
  functions,
  "confirmLessonRequest"
);
const rejectLessonCloudFunction = httpsCallable(
  functions,
  "rejectLessonRequest"
);
const requestCancellationCloudFunction = httpsCallable(
  functions,
  "requestLessonCancellation"
);
const processRefundCloudFunction = httpsCallable(
  functions,
  "processRefundDecision"
);
const completeLessonCloudFunction = httpsCallable(
  functions,
  "markLessonCompleted"
);

// Status utility functions

/**
 * Check if a user can confirm a lesson request
 * @param {Object} lesson - The lesson object
 * @param {string} userId - The user ID
 * @returns {boolean} - Whether the user can confirm the lesson
 */
export const canConfirmLesson = (lesson, userId) => {
  return (
    lesson.status === LESSON_STATUSES.REQUESTED && lesson.teacherId === userId
  );
};

/**
 * Check if a user can reject a lesson request
 * @param {Object} lesson - The lesson object
 * @param {string} userId - The user ID
 * @returns {boolean} - Whether the user can reject the lesson
 */
export const canRejectLesson = (lesson, userId) => {
  return (
    lesson.status === LESSON_STATUSES.REQUESTED && lesson.teacherId === userId
  );
};

/**
 * Check if a user can request cancellation of a lesson
 * @param {Object} lesson - The lesson object
 * @param {string} userId - The user ID
 * @returns {boolean} - Whether the user can request cancellation
 */
export const canRequestCancellation = (lesson, userId) => {
  return (
    lesson.status === LESSON_STATUSES.CONFIRMED &&
    (lesson.teacherId === userId || lesson.studentId === userId)
  );
};

/**
 * Check if a user can process a refund decision
 * @param {Object} lesson - The lesson object
 * @param {string} userId - The user ID
 * @returns {boolean} - Whether the user can process the refund decision
 */
export const canProcessRefundDecision = (lesson, userId) => {
  if (lesson.status !== LESSON_STATUSES.CANCELLATION_REQUESTED) {
    return false;
  }

  // The person who didn't request the cancellation can process the refund decision
  return (
    (lesson.cancellationRequestedBy === lesson.studentId &&
      userId === lesson.teacherId) ||
    (lesson.cancellationRequestedBy === lesson.teacherId &&
      userId === lesson.studentId)
  );
};

/**
 * Check if a user can mark a lesson as completed
 * @param {Object} lesson - The lesson object
 * @param {string} userId - The user ID
 * @returns {boolean} - Whether the user can complete the lesson
 */
export const canCompleteLesson = (lesson, userId) => {
  return (
    lesson.status === LESSON_STATUSES.CONFIRMED &&
    (lesson.teacherId === userId || lesson.studentId === userId)
  );
};

/**
 * Check if a lesson is editable (can be modified)
 * @param {Object} lesson - The lesson object
 * @returns {boolean} - Whether the lesson can be modified
 */
export const isLessonEditable = (lesson) => {
  return (
    lesson.status === LESSON_STATUSES.REQUESTED ||
    lesson.status === LESSON_STATUSES.CONFIRMED
  );
};

/**
 * Check if a lesson can be cancelled directly (without approval)
 * @param {Object} lesson - The lesson object
 * @returns {boolean} - Whether the lesson can be cancelled directly
 */
export const canCancelDirectly = (lesson) => {
  return lesson.status === LESSON_STATUSES.REQUESTED;
};

/**
 * Get user-friendly display name for lesson status
 * @param {string} status - The lesson status
 * @returns {string} - User-friendly status name
 */
export const getStatusDisplayName = (status) => {
  const statusDisplayNames = {
    // Pre-payment checkout states (booking gated on payment; see
    // createLessonRecord / stripeWebhook). Without these, an in-flight or
    // abandoned checkout renders as "Unknown" in the student's lesson list.
    [LESSON_STATUSES.PENDING_PAYMENT]: "Pending payment",
    [LESSON_STATUSES.PAYMENT_FAILED]: "Payment failed",
    [LESSON_STATUSES.REQUESTED]: "Waiting for Approval",
    [LESSON_STATUSES.CONFIRMED]: "Confirmed",
    [LESSON_STATUSES.REJECTED]: "Declined",
    [LESSON_STATUSES.CANCELLATION_REQUESTED]: "Cancellation Requested",
    [LESSON_STATUSES.CANCELLED]: "Cancelled",
    [LESSON_STATUSES.COMPLETED]: "Completed",
    [LESSON_STATUSES.REVIEWED]: "Reviewed",
  };

  return statusDisplayNames[status] || "Unknown";
};

/**
 * Get the next possible status transitions for a lesson
 * @param {Object} lesson - The lesson object
 * @param {string} userId - The current user ID
 * @returns {Array} - Array of possible status transitions
 */
export const getPossibleStatusTransitions = (lesson, userId) => {
  const transitions = [];

  if (canConfirmLesson(lesson, userId)) {
    transitions.push({
      action: "confirm",
      status: LESSON_STATUSES.CONFIRMED,
      label: "Confirm Lesson",
    });
  }

  if (canRejectLesson(lesson, userId)) {
    transitions.push({
      action: "reject",
      status: LESSON_STATUSES.CANCELLED,
      label: "Reject Lesson",
    });
  }

  if (canRequestCancellation(lesson, userId)) {
    transitions.push({
      action: "request_cancellation",
      status: LESSON_STATUSES.CANCELLATION_REQUESTED,
      label: "Request Cancellation",
    });
  }

  if (canProcessRefundDecision(lesson, userId)) {
    transitions.push(
      {
        action: "approve_refund",
        status: LESSON_STATUSES.CANCELLED,
        label: "Approve Refund",
      },
      {
        action: "reject_refund",
        status: LESSON_STATUSES.CONFIRMED,
        label: "Reject Cancellation",
      }
    );
  }

  if (canCompleteLesson(lesson, userId)) {
    transitions.push({
      action: "complete",
      status: LESSON_STATUSES.COMPLETED,
      label: "Mark Complete",
    });
  }

  return transitions;
};

/**
 * Validate status transition
 * @param {string} currentStatus - Current lesson status
 * @param {string} newStatus - Desired new status
 * @param {string} action - The action being performed
 * @returns {boolean} - Whether the transition is valid
 */
export const isValidStatusTransition = (currentStatus, newStatus, action) => {
  const validTransitions = {
    [LESSON_STATUSES.REQUESTED]: [
      { status: LESSON_STATUSES.CONFIRMED, action: "confirm" },
      { status: LESSON_STATUSES.CANCELLED, action: "reject" },
    ],
    [LESSON_STATUSES.CONFIRMED]: [
      {
        status: LESSON_STATUSES.CANCELLATION_REQUESTED,
        action: "request_cancellation",
      },
      { status: LESSON_STATUSES.COMPLETED, action: "complete" },
    ],
    [LESSON_STATUSES.CANCELLATION_REQUESTED]: [
      { status: LESSON_STATUSES.CANCELLED, action: "approve_refund" },
      { status: LESSON_STATUSES.CONFIRMED, action: "reject_refund" },
    ],
  };

  const allowedTransitions = validTransitions[currentStatus] || [];
  return allowedTransitions.some(
    (t) => t.status === newStatus && t.action === action
  );
};

/**
 * Get lessons by status for a user
 * @param {string} userId - The user ID
 * @param {string} userType - The user type ('student' or 'teacher')
 * @param {Array<string>} statuses - Array of statuses to filter by
 * @returns {Promise<Array>} - Filtered lessons
 */
export const getLessonsByStatus = async (userId, userType, statuses) => {
  Logger.log(
    "info",
    "Getting lessons by status",
    { userId, userType, statuses },
    "LessonService"
  );

  try {
    const fieldName = userType === "teacher" ? "teacherId" : "studentId";

    // For single status, we can use a direct query
    if (statuses.length === 1) {
      const q = query(
        collection(db, "lessons"),
        where(fieldName, "==", userId),
        where("status", "==", statuses[0]),
        orderBy("startTime", "desc")
      );

      const querySnapshot = await getDocs(q);
      const lessons = [];

      querySnapshot.forEach((doc) => {
        lessons.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      return lessons;
    }

    // For multiple statuses, get all lessons and filter client-side
    const allLessons =
      userType === "teacher"
        ? await getTeacherLessons(userId)
        : await getStudentLessons(userId);

    return allLessons.filter((lesson) => statuses.includes(lesson.status));
  } catch (error) {
    Logger.log(
      "error",
      "Error getting lessons by status",
      { error: error.message, userId, userType, statuses },
      "LessonService"
    );
    throw error;
  }
};

/**
 * Get pending lesson requests for a teacher
 * @param {string} teacherId - The teacher ID
 * @returns {Promise<Array>} - Pending lesson requests
 */
export const getPendingLessonRequests = async (teacherId) => {
  return getLessonsByStatus(teacherId, "teacher", [LESSON_STATUSES.REQUESTED]);
};

/**
 * Get confirmed lessons for a user
 * @param {string} userId - The user ID
 * @param {string} userType - The user type ('student' or 'teacher')
 * @returns {Promise<Array>} - Confirmed lessons
 */
export const getConfirmedLessons = async (userId, userType) => {
  return getLessonsByStatus(userId, userType, [LESSON_STATUSES.CONFIRMED]);
};

/**
 * Get completed lessons for a user
 * @param {string} userId - The user ID
 * @param {string} userType - The user type ('student' or 'teacher')
 * @returns {Promise<Array>} - Completed lessons
 */
export const getCompletedLessons = async (userId, userType) => {
  return getLessonsByStatus(userId, userType, [LESSON_STATUSES.COMPLETED]);
};

/**
 * Get lessons requiring action from a user
 * @param {string} userId - The user ID
 * @param {string} userType - The user type ('student' or 'teacher')
 * @returns {Promise<Array>} - Lessons requiring action
 */
export const getLessonsRequiringAction = async (userId, userType) => {
  const lessons = [];

  if (userType === "teacher") {
    // Teachers need to act on requested lessons and cancellation requests
    const requestedLessons = await getLessonsByStatus(userId, "teacher", [
      LESSON_STATUSES.REQUESTED,
    ]);
    const cancellationRequests = await getLessonsByStatus(userId, "teacher", [
      LESSON_STATUSES.CANCELLATION_REQUESTED,
    ]).then((allLessons) =>
      allLessons.filter(
        (lesson) => lesson.cancellationRequestedBy === lesson.studentId
      )
    );

    lessons.push(...requestedLessons, ...cancellationRequests);
  } else {
    // Students need to act on cancellation requests from teachers
    const cancellationRequests = await getLessonsByStatus(userId, "student", [
      LESSON_STATUSES.CANCELLATION_REQUESTED,
    ]).then((allLessons) =>
      allLessons.filter(
        (lesson) => lesson.cancellationRequestedBy === lesson.teacherId
      )
    );

    lessons.push(...cancellationRequests);
  }

  // Sort by most urgent first (earliest lessons first)
  return lessons.sort((a, b) => {
    const aTime = a.startTime?.toDate?.() || new Date(a.startTime);
    const bTime = b.startTime?.toDate?.() || new Date(b.startTime);
    return aTime - bTime;
  });
};

/**
 * Validates if a string is a valid URL
 * @param {string} url - The URL to validate
 * @returns {boolean} - True if URL is valid
 */
const isValidURL = (url) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

/**
 * Book a lesson
 * @param {Object} lessonData - The lesson data
 * @returns {Promise<string>} - The ID of the created lesson
 */
export const bookLesson = async (lessonData) => {
  Logger.log(
    "info",
    "Booking lesson",
    { teacherId: lessonData.teacherId, studentId: lessonData.studentId },
    "LessonService"
  );

  try {
    if (!FeatureFlags.core.instantBooking) {
      throw new Error("Lesson booking feature is disabled");
    }

    const lessonRef = await addDoc(collection(db, "lessons"), {
      ...lessonData,
      status: LESSON_STATUSES.REQUESTED,
      createdAt: new Date(),
    });

    Logger.log(
      "info",
      "Successfully booked lesson",
      { lessonId: lessonRef.id, teacherId: lessonData.teacherId },
      "LessonService"
    );

    // Send notifications about lesson request
    try {
      const lessonWithId = { id: lessonRef.id, ...lessonData };

      // Notify student about lesson request submission
      await NotificationService.sendNotification(
        lessonData.studentId,
        NOTIFICATION_TYPES.LESSON_REQUESTED,
        {
          lessonId: lessonRef.id,
          lessonData: lessonWithId,
          subject: lessonData.subject || "Music Lesson",
          startTime: lessonData.startTime,
          endTime: lessonData.endTime,
          teacherName: lessonData.teacherName || "Your Teacher",
        }
      );

      // Notify teacher about new lesson request
      await NotificationService.sendNotification(
        lessonData.teacherId,
        NOTIFICATION_TYPES.LESSON_REQUEST_RECEIVED,
        {
          lessonId: lessonRef.id,
          lessonData: lessonWithId,
          subject: lessonData.subject || "Music Lesson",
          startTime: lessonData.startTime,
          endTime: lessonData.endTime,
          studentName: lessonData.studentName || "Your Student",
        }
      );

      Logger.log(
        "info",
        "Successfully sent lesson request notifications",
        { lessonId: lessonRef.id },
        "LessonService"
      );
    } catch (notificationError) {
      // Don't fail the lesson booking if notifications fail
      Logger.log(
        "error",
        "Failed to send lesson request notifications",
        {
          lessonId: lessonRef.id,
          error: notificationError.message,
        },
        "LessonService"
      );
    }

    // Note: Reminders will be scheduled when the lesson is confirmed by the teacher

    return lessonRef.id;
  } catch (error) {
    Logger.log(
      "error",
      "Error booking lesson",
      { error: error.message, teacherId: lessonData.teacherId },
      "LessonService"
    );
    throw error;
  }
};

/**
 * Get a lesson by ID
 * @param {string} lessonId - The lesson ID
 * @returns {Promise<Object>} - The lesson data
 */
export const getLessonById = async (lessonId) => {
  Logger.log("info", "Fetching lesson by ID", { lessonId }, "LessonService");

  try {
    const lessonDoc = await getDoc(doc(db, "lessons", lessonId));

    if (!lessonDoc.exists()) {
      Logger.log("error", "Lesson not found", { lessonId }, "LessonService");
      throw new Error("Lesson not found");
    }

    const lesson = {
      id: lessonDoc.id,
      ...lessonDoc.data(),
    };

    Logger.log(
      "info",
      "Successfully retrieved lesson",
      { lessonId, status: lesson.status },
      "LessonService"
    );

    return lesson;
  } catch (error) {
    Logger.log(
      "error",
      "Error getting lesson",
      { error: error.message, lessonId },
      "LessonService"
    );
    throw error;
  }
};

/**
 * Update a lesson
 * @param {string} lessonId - The lesson ID
 * @param {Object} data - The data to update
 * @returns {Promise<boolean>} - Success status
 */
export const updateLesson = async (lessonId, data) => {
  Logger.log(
    "info",
    "Updating lesson",
    { lessonId, updateFields: Object.keys(data) },
    "LessonService"
  );

  try {
    await updateDoc(doc(db, "lessons", lessonId), {
      ...data,
      updatedAt: new Date(),
    });

    Logger.log(
      "info",
      "Successfully updated lesson",
      { lessonId },
      "LessonService"
    );

    return true;
  } catch (error) {
    Logger.log(
      "error",
      "Error updating lesson",
      { error: error.message, lessonId },
      "LessonService"
    );
    throw error;
  }
};

/**
 * Cancel a lesson
 * @param {string} lessonId - The lesson ID
 * @param {string} canceledBy - The user ID who canceled
 * @param {string} reason - The cancellation reason
 * @returns {Promise<boolean>} - Success status
 */
export const cancelLesson = async (lessonId, canceledBy, reason = "") => {
  Logger.log(
    "info",
    "Canceling lesson",
    { lessonId, canceledBy, hasReason: !!reason },
    "LessonService"
  );

  try {
    if (!FeatureFlags.core.lessonCancellation) {
      throw new Error("Lesson cancellation feature is disabled");
    }

    await updateDoc(doc(db, "lessons", lessonId), {
      status: LESSON_STATUSES.CANCELLED,
      canceledBy,
      cancellationReason: reason,
      canceledAt: new Date(),
    });

    Logger.log(
      "info",
      "Successfully canceled lesson",
      { lessonId, canceledBy },
      "LessonService"
    );

    // Send cancellation notifications
    try {
      // Get the lesson data to notify the other party
      const lesson = await getLessonById(lessonId);

      const notificationData = {
        lessonId,
        lessonData: lesson,
        subject: lesson.subject || "Music Lesson",
        startTime: lesson.startTime,
        endTime: lesson.endTime,
        cancellationReason: reason,
        canceledAt: new Date(),
      };

      // Notify student if teacher canceled, or teacher if student canceled
      if (canceledBy === lesson.teacherId) {
        // Teacher canceled - notify student
        await NotificationService.sendNotification(
          lesson.studentId,
          NOTIFICATION_TYPES.LESSON_CANCELLED,
          {
            ...notificationData,
            canceledByName: lesson.teacherName || "Your Teacher",
            canceledByRole: "teacher",
          }
        );
      } else if (canceledBy === lesson.studentId) {
        // Student canceled - notify teacher
        await NotificationService.sendNotification(
          lesson.teacherId,
          NOTIFICATION_TYPES.LESSON_CANCELLED,
          {
            ...notificationData,
            canceledByName: lesson.studentName || "Your Student",
            canceledByRole: "student",
          }
        );
      }

      Logger.log(
        "info",
        "Successfully sent lesson cancellation notifications",
        { lessonId, canceledBy },
        "LessonService"
      );
    } catch (notificationError) {
      // Don't fail the cancellation if notifications fail
      Logger.log(
        "error",
        "Failed to send lesson cancellation notifications",
        {
          lessonId,
          canceledBy,
          error: notificationError.message,
        },
        "LessonService"
      );
    }

    // Cancel scheduled reminders for the lesson
    try {
      await ReminderService.cancelRemindersForLesson(lessonId);

      Logger.log(
        "info",
        "Successfully cancelled lesson reminders",
        { lessonId },
        "LessonService"
      );
    } catch (reminderError) {
      // Don't fail the cancellation if reminder cancellation fails
      Logger.log(
        "error",
        "Failed to cancel lesson reminders",
        {
          lessonId,
          error: reminderError.message,
        },
        "LessonService"
      );
    }

    return true;
  } catch (error) {
    Logger.log(
      "error",
      "Error canceling lesson",
      { error: error.message, lessonId, canceledBy },
      "LessonService"
    );
    throw error;
  }
};

/**
 * Confirm a lesson request (teacher approves student's lesson request)
 * @param {string} lessonId - The lesson ID
 * @param {string} teacherId - The teacher ID
 * @returns {Promise<boolean>} - Success status
 */
export const confirmLessonRequest = async (lessonId, teacherId) => {
  Logger.log(
    "info",
    "Confirming lesson request",
    { lessonId, teacherId },
    "LessonService"
  );

  try {
    if (!FeatureFlags.core.instantBooking) {
      throw new Error("Lesson booking feature is disabled");
    }

    // Verify the lesson exists and teacher has permission
    const lesson = await getLessonById(lessonId);

    if (lesson.teacherId !== teacherId) {
      throw new Error("Only the lesson teacher can confirm requests");
    }

    if (lesson.status !== LESSON_STATUSES.REQUESTED) {
      throw new Error("Only requested lessons can be confirmed");
    }

    // Call Cloud Function to handle confirmation workflow
    try {
      const result = await confirmLessonCloudFunction({
        lessonId,
        teacherId,
      });

      Logger.log(
        "info",
        "Cloud function confirmLesson completed",
        { lessonId, teacherId, result: result.data },
        "LessonService"
      );
    } catch (cloudError) {
      // If Cloud Function doesn't exist, handle locally
      Logger.log(
        "warn",
        "Cloud function not available, handling confirmation locally",
        { lessonId, cloudError: cloudError.message },
        "LessonService"
      );

      // Update lesson status to confirmed
      await updateDoc(doc(db, "lessons", lessonId), {
        status: LESSON_STATUSES.CONFIRMED,
        confirmedAt: new Date(),
        confirmedBy: teacherId,
        updatedAt: new Date(),
      });

      // Schedule reminders for confirmed lesson
      try {
        const confirmedLesson = await getLessonById(lessonId);
        await ReminderService.scheduleRemindersForLesson(confirmedLesson);

        Logger.log(
          "info",
          "Successfully scheduled reminders for confirmed lesson",
          { lessonId },
          "LessonService"
        );
      } catch (reminderError) {
        Logger.log(
          "error",
          "Failed to schedule reminders for confirmed lesson",
          { lessonId, error: reminderError.message },
          "LessonService"
        );
      }

      // Send confirmation notifications
      try {
        await NotificationService.sendNotification(
          lesson.studentId,
          NOTIFICATION_TYPES.LESSON_CONFIRMED,
          {
            lessonId,
            lessonData: lesson,
            subject: lesson.subject || "Music Lesson",
            startTime: lesson.startTime,
            endTime: lesson.endTime,
            teacherName: lesson.teacherName || "Your Teacher",
            confirmedAt: new Date(),
          }
        );

        Logger.log(
          "info",
          "Successfully sent lesson confirmation notification",
          { lessonId },
          "LessonService"
        );
      } catch (notificationError) {
        Logger.log(
          "error",
          "Failed to send lesson confirmation notification",
          { lessonId, error: notificationError.message },
          "LessonService"
        );
      }
    }

    Logger.log(
      "info",
      "Successfully confirmed lesson request",
      { lessonId, teacherId },
      "LessonService"
    );

    return true;
  } catch (error) {
    Logger.log(
      "error",
      "Error confirming lesson request",
      { error: error.message, lessonId, teacherId },
      "LessonService"
    );
    throw error;
  }
};

/**
 * Reject a lesson request (teacher rejects student's lesson request)
 * @param {string} lessonId - The lesson ID
 * @param {string} teacherId - The teacher ID
 * @param {string} reason - The rejection reason
 * @returns {Promise<boolean>} - Success status
 */
export const rejectLessonRequest = async (lessonId, teacherId, reason = "") => {
  Logger.log(
    "info",
    "Rejecting lesson request",
    { lessonId, teacherId, hasReason: !!reason },
    "LessonService"
  );

  try {
    if (!FeatureFlags.core.instantBooking) {
      throw new Error("Lesson booking feature is disabled");
    }

    // Verify the lesson exists and teacher has permission
    const lesson = await getLessonById(lessonId);

    if (lesson.teacherId !== teacherId) {
      throw new Error("Only the lesson teacher can reject requests");
    }

    if (lesson.status !== LESSON_STATUSES.REQUESTED) {
      throw new Error("Only requested lessons can be rejected");
    }

    // Call Cloud Function to handle rejection workflow
    try {
      const result = await rejectLessonCloudFunction({
        lessonId,
        teacherId,
        reason,
      });

      Logger.log(
        "info",
        "Cloud function rejectLesson completed",
        { lessonId, teacherId, result: result.data },
        "LessonService"
      );
    } catch (cloudError) {
      // If Cloud Function doesn't exist, handle locally
      Logger.log(
        "warn",
        "Cloud function not available, handling rejection locally",
        { lessonId, cloudError: cloudError.message },
        "LessonService"
      );

      // Update lesson status to rejected
      await updateDoc(doc(db, "lessons", lessonId), {
        status: LESSON_STATUSES.REJECTED,
        rejectedAt: new Date(),
        rejectedBy: teacherId,
        rejectionReason: reason,
        updatedAt: new Date(),
      });

      // Send rejection notification to student
      try {
        await NotificationService.sendNotification(
          lesson.studentId,
          NOTIFICATION_TYPES.LESSON_REJECTED,
          {
            lessonId,
            lessonData: lesson,
            subject: lesson.subject || "Music Lesson",
            startTime: lesson.startTime,
            endTime: lesson.endTime,
            teacherName: lesson.teacherName || "Your Teacher",
            rejectionReason: reason,
            rejectedAt: new Date(),
          }
        );

        Logger.log(
          "info",
          "Successfully sent lesson rejection notification",
          { lessonId },
          "LessonService"
        );
      } catch (notificationError) {
        Logger.log(
          "error",
          "Failed to send lesson rejection notification",
          { lessonId, error: notificationError.message },
          "LessonService"
        );
      }
    }

    Logger.log(
      "info",
      "Successfully rejected lesson request",
      { lessonId, teacherId },
      "LessonService"
    );

    return true;
  } catch (error) {
    Logger.log(
      "error",
      "Error rejecting lesson request",
      { error: error.message, lessonId, teacherId },
      "LessonService"
    );
    throw error;
  }
};

/**
 * Request cancellation of a confirmed lesson
 * @param {string} lessonId - The lesson ID
 * @param {string} requestedBy - The user ID who requested cancellation
 * @param {string} reason - The cancellation reason
 * @returns {Promise<boolean>} - Success status
 */
export const requestCancellation = async (
  lessonId,
  requestedBy,
  reason = ""
) => {
  Logger.log(
    "info",
    "Requesting lesson cancellation",
    { lessonId, requestedBy, hasReason: !!reason },
    "LessonService"
  );

  try {
    if (!FeatureFlags.core.lessonCancellation) {
      throw new Error("Lesson cancellation feature is disabled");
    }

    // Verify the lesson exists and user has permission
    const lesson = await getLessonById(lessonId);

    if (lesson.teacherId !== requestedBy && lesson.studentId !== requestedBy) {
      throw new Error("Only lesson participants can request cancellation");
    }

    if (lesson.status !== LESSON_STATUSES.CONFIRMED) {
      throw new Error("Only confirmed lessons can be cancelled");
    }

    // Call Cloud Function to handle cancellation request workflow
    try {
      const result = await requestCancellationCloudFunction({
        lessonId,
        requestedBy,
        reason,
      });

      Logger.log(
        "info",
        "Cloud function requestCancellation completed",
        { lessonId, requestedBy, result: result.data },
        "LessonService"
      );
    } catch (cloudError) {
      // If Cloud Function doesn't exist, handle locally
      Logger.log(
        "warn",
        "Cloud function not available, handling cancellation request locally",
        { lessonId, cloudError: cloudError.message },
        "LessonService"
      );

      // Update lesson status to cancellation_requested
      await updateDoc(doc(db, "lessons", lessonId), {
        status: LESSON_STATUSES.CANCELLATION_REQUESTED,
        cancellationRequestedBy: requestedBy,
        cancellationRequestedAt: new Date(),
        cancellationReason: reason,
        updatedAt: new Date(),
      });

      // Determine who to notify (the other party)
      const recipientId =
        requestedBy === lesson.teacherId ? lesson.studentId : lesson.teacherId;
      // const recipientName = requestedBy === lesson.teacherId ? lesson.studentName : lesson.teacherName;
      const requesterRole =
        requestedBy === lesson.teacherId ? "teacher" : "student";

      // Send cancellation request notification
      try {
        await NotificationService.sendNotification(
          recipientId,
          NOTIFICATION_TYPES.CANCELLATION_REQUESTED,
          {
            lessonId,
            lessonData: lesson,
            subject: lesson.subject || "Music Lesson",
            startTime: lesson.startTime,
            endTime: lesson.endTime,
            requestedByRole: requesterRole,
            cancellationReason: reason,
            requestedAt: new Date(),
          }
        );

        Logger.log(
          "info",
          "Successfully sent cancellation request notification",
          { lessonId, requestedBy, recipientId },
          "LessonService"
        );
      } catch (notificationError) {
        Logger.log(
          "error",
          "Failed to send cancellation request notification",
          { lessonId, error: notificationError.message },
          "LessonService"
        );
      }
    }

    Logger.log(
      "info",
      "Successfully requested lesson cancellation",
      { lessonId, requestedBy },
      "LessonService"
    );

    return true;
  } catch (error) {
    Logger.log(
      "error",
      "Error requesting lesson cancellation",
      { error: error.message, lessonId, requestedBy },
      "LessonService"
    );
    throw error;
  }
};

/**
 * Process refund decision for a cancelled lesson
 * @param {string} lessonId - The lesson ID
 * @param {string} decidedBy - The user ID who made the decision
 * @param {boolean} approveRefund - Whether to approve the refund
 * @param {string} teacherNotes - Optional notes from teacher
 * @returns {Promise<boolean>} - Success status
 */
export const processRefundDecision = async (
  lessonId,
  decidedBy,
  approveRefund,
  teacherNotes = ""
) => {
  Logger.log(
    "info",
    "Processing refund decision",
    { lessonId, decidedBy, approveRefund, hasNotes: !!teacherNotes },
    "LessonService"
  );

  try {
    // Verify the lesson exists and user has permission
    const lesson = await getLessonById(lessonId);

    if (lesson.status !== LESSON_STATUSES.CANCELLATION_REQUESTED) {
      throw new Error(
        "Can only process refund for lessons with cancellation requests"
      );
    }

    // Determine who can approve refunds based on who requested cancellation
    const canApprove =
      (lesson.cancellationRequestedBy === lesson.studentId &&
        decidedBy === lesson.teacherId) ||
      (lesson.cancellationRequestedBy === lesson.teacherId &&
        decidedBy === lesson.studentId);

    if (!canApprove) {
      throw new Error(
        "Only the other party can approve or reject the cancellation request"
      );
    }

    // Call Cloud Function to handle refund decision workflow
    try {
      const result = await processRefundCloudFunction({
        lessonId,
        decidedBy,
        approveRefund,
        teacherNotes,
      });

      Logger.log(
        "info",
        "Cloud function processRefund completed",
        { lessonId, decidedBy, approveRefund, result: result.data },
        "LessonService"
      );
    } catch (cloudError) {
      // If Cloud Function doesn't exist, handle locally
      Logger.log(
        "warn",
        "Cloud function not available, handling refund decision locally",
        { lessonId, cloudError: cloudError.message },
        "LessonService"
      );

      // Update lesson status based on decision
      const updateData = {
        status: LESSON_STATUSES.CANCELLED,
        refundDecisionBy: decidedBy,
        refundDecisionAt: new Date(),
        refundApproved: approveRefund,
        updatedAt: new Date(),
      };

      if (teacherNotes) {
        updateData.refundDecisionNotes = teacherNotes;
      }

      await updateDoc(doc(db, "lessons", lessonId), updateData);

      // Cancel reminders for cancelled lesson
      try {
        await ReminderService.cancelRemindersForLesson(lessonId);
        Logger.log(
          "info",
          "Successfully cancelled lesson reminders",
          { lessonId },
          "LessonService"
        );
      } catch (reminderError) {
        Logger.log(
          "error",
          "Failed to cancel lesson reminders",
          { lessonId, error: reminderError.message },
          "LessonService"
        );
      }

      // Send refund decision notification
      try {
        const recipientId = lesson.cancellationRequestedBy;
        const notificationType = approveRefund
          ? NOTIFICATION_TYPES.REFUND_APPROVED
          : NOTIFICATION_TYPES.REFUND_REJECTED;

        await NotificationService.sendNotification(
          recipientId,
          notificationType,
          {
            lessonId,
            lessonData: lesson,
            subject: lesson.subject || "Music Lesson",
            startTime: lesson.startTime,
            endTime: lesson.endTime,
            refundApproved: approveRefund,
            refundDecisionNotes: teacherNotes,
            decidedAt: new Date(),
          }
        );

        Logger.log(
          "info",
          "Successfully sent refund decision notification",
          { lessonId, approveRefund },
          "LessonService"
        );
      } catch (notificationError) {
        Logger.log(
          "error",
          "Failed to send refund decision notification",
          { lessonId, error: notificationError.message },
          "LessonService"
        );
      }
    }

    Logger.log(
      "info",
      "Successfully processed refund decision",
      { lessonId, decidedBy, approveRefund },
      "LessonService"
    );

    return true;
  } catch (error) {
    Logger.log(
      "error",
      "Error processing refund decision",
      { error: error.message, lessonId, decidedBy },
      "LessonService"
    );
    throw error;
  }
};

/**
 * Request to reschedule a lesson
 * @param {string} lessonId - The lesson ID
 * @param {string} requestedBy - The user ID who requested
 * @param {Date} proposedStartTime - The proposed new start time
 * @param {Date} proposedEndTime - The proposed new end time
 * @returns {Promise<boolean>} - Success status
 */
export const requestRescheduleLesson = async (
  lessonId,
  requestedBy,
  proposedStartTime,
  proposedEndTime
) => {
  Logger.log(
    "info",
    "Requesting lesson reschedule",
    { lessonId, requestedBy, proposedStartTime },
    "LessonService"
  );

  try {
    if (!FeatureFlags.core.lessonReschedule) {
      throw new Error("Lesson reschedule feature is disabled");
    }

    await updateDoc(doc(db, "lessons", lessonId), {
      status: "reschedule_requested",
      rescheduleRequestedBy: requestedBy,
      proposedStartTime,
      proposedEndTime,
      rescheduleRequestedAt: new Date(),
    });

    Logger.log(
      "info",
      "Successfully requested lesson reschedule",
      { lessonId, requestedBy },
      "LessonService"
    );

    return true;
  } catch (error) {
    Logger.log(
      "error",
      "Error requesting reschedule",
      { error: error.message, lessonId, requestedBy },
      "LessonService"
    );
    throw error;
  }
};

/**
 * Respond to a reschedule request
 * @param {string} lessonId - The lesson ID
 * @param {string} respondedBy - The user ID who responded
 * @param {boolean} accepted - Whether the reschedule was accepted
 * @returns {Promise<boolean>} - Success status
 */
export const respondToReschedule = async (lessonId, respondedBy, accepted) => {
  Logger.log(
    "info",
    "Responding to reschedule request",
    { lessonId, respondedBy, accepted },
    "LessonService"
  );

  try {
    if (!FeatureFlags.core.lessonReschedule) {
      throw new Error("Lesson reschedule feature is disabled");
    }
    if (accepted) {
      // Get the lesson to find the proposed times
      const lesson = await getLessonById(lessonId);

      await updateDoc(doc(db, "lessons", lessonId), {
        status: LESSON_STATUSES.CONFIRMED,
        startTime: lesson.proposedStartTime,
        endTime: lesson.proposedEndTime,
        rescheduleRespondedBy: respondedBy,
        rescheduleAccepted: true,
        rescheduleRespondedAt: new Date(),
      });
    } else {
      await updateDoc(doc(db, "lessons", lessonId), {
        status: "reschedule_rejected",
        rescheduleRespondedBy: respondedBy,
        rescheduleAccepted: false,
        rescheduleRespondedAt: new Date(),
      });
    }
    Logger.log(
      "info",
      "Successfully responded to reschedule request",
      { lessonId, respondedBy, accepted },
      "LessonService"
    );

    // Send reschedule response notifications
    try {
      const lesson = await getLessonById(lessonId);

      const notificationData = {
        lessonId,
        lessonData: lesson,
        subject: lesson.subject || "Music Lesson",
        originalStartTime: lesson.startTime,
        originalEndTime: lesson.endTime,
        accepted,
        respondedAt: new Date(),
      };

      if (accepted) {
        // Reschedule was accepted - notify the requester
        notificationData.newStartTime = lesson.proposedStartTime;
        notificationData.newEndTime = lesson.proposedEndTime;

        if (lesson.rescheduleRequestedBy === lesson.studentId) {
          // Student requested, teacher accepted - notify student
          await NotificationService.sendNotification(
            lesson.studentId,
            NOTIFICATION_TYPES.LESSON_RESCHEDULED,
            {
              ...notificationData,
              respondedByName: lesson.teacherName || "Your Teacher",
              respondedByRole: "teacher",
            }
          );
        } else if (lesson.rescheduleRequestedBy === lesson.teacherId) {
          // Teacher requested, student accepted - notify teacher
          await NotificationService.sendNotification(
            lesson.teacherId,
            NOTIFICATION_TYPES.LESSON_RESCHEDULED,
            {
              ...notificationData,
              respondedByName: lesson.studentName || "Your Student",
              respondedByRole: "student",
            }
          );
        }

        Logger.log(
          "info",
          "Successfully sent lesson reschedule confirmation notifications",
          { lessonId, respondedBy },
          "LessonService"
        );
      }
      // Note: We might add rejected reschedule notifications in the future if needed
    } catch (notificationError) {
      // Don't fail the reschedule response if notifications fail
      Logger.log(
        "error",
        "Failed to send reschedule response notifications",
        {
          lessonId,
          respondedBy,
          accepted,
          error: notificationError.message,
        },
        "LessonService"
      );
    }

    // Reschedule reminders if the reschedule was accepted
    if (accepted) {
      try {
        const updatedLesson = await getLessonById(lessonId);
        await ReminderService.rescheduleRemindersForLesson(
          lessonId,
          updatedLesson
        );

        Logger.log(
          "info",
          "Successfully rescheduled lesson reminders",
          { lessonId, respondedBy },
          "LessonService"
        );
      } catch (reminderError) {
        // Don't fail the reschedule response if reminder rescheduling fails
        Logger.log(
          "error",
          "Failed to reschedule lesson reminders",
          {
            lessonId,
            respondedBy,
            error: reminderError.message,
          },
          "LessonService"
        );
      }
    }

    return true;
  } catch (error) {
    Logger.log(
      "error",
      "Error responding to reschedule",
      { error: error.message, lessonId, respondedBy },
      "LessonService"
    );
    throw error;
  }
};

/**
 * Get student lessons
 * @param {string} studentId - The student ID
 * @returns {Promise<Array>} - The student's lessons
 */
export const getStudentLessons = async (studentId) => {
  try {
    // Security check: ensure the requesting user can only get their own lessons
    const currentUser = auth.currentUser;
    if (!currentUser || currentUser.uid !== studentId) {
      throw new Error("Unauthorized: Can only fetch your own lessons");
    }

    Logger.log(
      "debug",
      "Starting student lessons query",
      { studentId, currentUserUid: currentUser.uid },
      "LessonService"
    );

    // Strategy 1: Get individual lessons where user is the student
    const individualLessonsQuery = query(
      collection(db, "lessons"),
      where("studentId", "==", studentId),
      where("type", "==", "individual")
    );

    // Strategy 2: Get group lessons where user is enrolled.
    // Both filters are required: security rules enforce type == 'group' for
    // array-contains queries, and the composite index covers both fields.
    const enrolledGroupLessonsQuery = query(
      collection(db, "lessons"),
      where("type", "==", "group"),
      where("currentStudents", "array-contains", studentId)
    );

    // Strategy 3: Fallback query for lessons without type field or with missing type
    // This query will only return lessons that pass Firestore security rules
    const fallbackLessonsQuery = query(
      collection(db, "lessons"),
      where("studentId", "==", studentId)
    );

    // Strategy 4: Additional query for lessons where student might be the teacher
    // This covers edge cases where the lesson was created with wrong field mapping
    const teacherLessonsQuery = query(
      collection(db, "lessons"),
      where("teacherId", "==", studentId)
    );

    Logger.log(
      "debug",
      "Executing parallel queries",
      { studentId },
      "LessonService"
    );

    const [
      individualSnapshot,
      enrolledGroupSnapshot,
      fallbackSnapshot,
      teacherSnapshot,
    ] = await Promise.all([
      getDocs(individualLessonsQuery).catch((err) => {
        Logger.log(
          "warn",
          "Individual lessons query failed",
          { error: err.message },
          "LessonService"
        );
        return { size: 0, forEach: () => {} };
      }),
      getDocs(enrolledGroupLessonsQuery).catch((err) => {
        Logger.log(
          "warn",
          "Group lessons query failed",
          { error: err.message },
          "LessonService"
        );
        return { size: 0, forEach: () => {} };
      }),
      getDocs(fallbackLessonsQuery).catch((err) => {
        Logger.log(
          "warn",
          "Fallback lessons query failed",
          { error: err.message },
          "LessonService"
        );
        return { size: 0, forEach: () => {} };
      }),
      getDocs(teacherLessonsQuery).catch((err) => {
        Logger.log(
          "warn",
          "Teacher lessons query failed",
          { error: err.message },
          "LessonService"
        );
        return { size: 0, forEach: () => {} };
      }),
    ]);

    Logger.log(
      "debug",
      "Query results summary",
      {
        studentId,
        individualCount: individualSnapshot.size,
        enrolledGroupCount: enrolledGroupSnapshot.size,
        fallbackCount: fallbackSnapshot.size,
        teacherCount: teacherSnapshot.size,
      },
      "LessonService"
    );

    const lessons = [];
    const seenLessonIds = new Set();

    // Process individual lessons
    if (individualSnapshot.forEach) {
      individualSnapshot.forEach((doc) => {
        if (!seenLessonIds.has(doc.id)) {
          const lessonData = doc.data();
          Logger.log(
            "debug",
            "Processing individual lesson",
            {
              lessonId: doc.id,
              type: lessonData.type,
              studentId: lessonData.studentId,
              teacherId: lessonData.teacherId,
              hasType: "type" in lessonData,
              fields: Object.keys(lessonData).sort(),
            },
            "LessonService"
          );
          lessons.push({
            id: doc.id,
            ...lessonData,
            source: "individual_query",
          });
          seenLessonIds.add(doc.id);
        }
      });
    }

    // Process enrolled group lessons
    if (enrolledGroupSnapshot.forEach) {
      enrolledGroupSnapshot.forEach((doc) => {
        if (!seenLessonIds.has(doc.id)) {
          const lessonData = doc.data();
          Logger.log(
            "debug",
            "Processing group lesson",
            {
              lessonId: doc.id,
              type: lessonData.type,
              currentStudents: lessonData.currentStudents,
              hasType: "type" in lessonData,
              fields: Object.keys(lessonData).sort(),
            },
            "LessonService"
          );
          // Normalize date fields: group lessons store the date as scheduledAt
          // while the dashboard expects startTime/endTime as JS Date objects.
          const rawStart = lessonData.startTime || lessonData.scheduledAt;
          const startTime = rawStart?.toDate
            ? rawStart.toDate()
            : new Date(rawStart);
          const durationMs = (lessonData.duration || 60) * 60 * 1000;
          const rawEnd = lessonData.endTime;
          const endTime = rawEnd
            ? rawEnd?.toDate
              ? rawEnd.toDate()
              : new Date(rawEnd)
            : new Date(startTime.getTime() + durationMs);
          lessons.push({
            id: doc.id,
            ...lessonData,
            startTime,
            endTime,
            source: "group_query",
          });
          seenLessonIds.add(doc.id);
        }
      });
    }

    // Process fallback lessons (lessons that weren't caught by type-specific queries)
    if (fallbackSnapshot.forEach) {
      fallbackSnapshot.forEach((doc) => {
        if (!seenLessonIds.has(doc.id)) {
          const lessonData = doc.data();
          Logger.log(
            "warn",
            "Found lesson in fallback that wasn't caught by type queries - possible data inconsistency",
            {
              lessonId: doc.id,
              type: lessonData.type,
              studentId: lessonData.studentId,
              teacherId: lessonData.teacherId,
              hasType: "type" in lessonData,
              currentStudents: lessonData.currentStudents,
              status: lessonData.status,
              fields: Object.keys(lessonData).sort(),
              securityRuleIssue:
                !lessonData.type ||
                !["individual", "group"].includes(lessonData.type),
            },
            "LessonService"
          );

          // Add this lesson but mark it for potential data fixing
          lessons.push({
            id: doc.id,
            ...lessonData,
            source: "fallback_query",
            needsTypeFieldFix:
              !lessonData.type ||
              !["individual", "group"].includes(lessonData.type),
          });
          seenLessonIds.add(doc.id);
        }
      });
    }

    // Process teacher lessons (edge case where student ID was mapped to teacher field)
    if (teacherSnapshot.forEach) {
      teacherSnapshot.forEach((doc) => {
        if (!seenLessonIds.has(doc.id)) {
          const lessonData = doc.data();
          Logger.log(
            "warn",
            "Found lesson where student is listed as teacher - possible data mapping issue",
            {
              lessonId: doc.id,
              type: lessonData.type,
              studentId: lessonData.studentId,
              teacherId: lessonData.teacherId,
              hasType: "type" in lessonData,
              fields: Object.keys(lessonData).sort(),
            },
            "LessonService"
          );

          lessons.push({
            id: doc.id,
            ...lessonData,
            source: "teacher_query",
            possibleDataMappingIssue: true,
          });
          seenLessonIds.add(doc.id);
        }
      });
    }

    // Sort by startTime descending (client-side sorting)
    lessons.sort((a, b) => {
      const aTime = a.startTime?.toDate?.() || new Date(a.startTime);
      const bTime = b.startTime?.toDate?.() || new Date(b.startTime);
      return bTime - aTime;
    });

    // Check for lessons that need data fixes and attempt to fix them
    const lessonsNeedingFixes = lessons.filter(
      (lesson) => lesson.needsTypeFieldFix || lesson.possibleDataMappingIssue
    );
    if (lessonsNeedingFixes.length > 0) {
      Logger.log(
        "warn",
        "Found lessons that may need data consistency fixes",
        {
          studentId,
          lessonsNeedingFixes: lessonsNeedingFixes.map((l) => ({
            id: l.id,
            type: l.type,
            needsTypeFieldFix: l.needsTypeFieldFix,
            possibleDataMappingIssue: l.possibleDataMappingIssue,
            source: l.source,
          })),
        },
        "LessonService"
      );

      // Attempt to fix lessons with missing or incorrect type fields
      const fixPromises = lessonsNeedingFixes
        .filter((lesson) => lesson.needsTypeFieldFix)
        .map(async (lesson) => {
          try {
            const fixed = await fixLessonTypeField(lesson.id, lesson);
            if (fixed) {
              Logger.log(
                "info",
                "Successfully auto-fixed lesson type field",
                { lessonId: lesson.id, studentId },
                "LessonService"
              );
            }
            return { lessonId: lesson.id, fixed };
          } catch (error) {
            Logger.log(
              "error",
              "Failed to auto-fix lesson type field",
              { lessonId: lesson.id, error: error.message, studentId },
              "LessonService"
            );
            return { lessonId: lesson.id, fixed: false, error: error.message };
          }
        });

      // Execute all fix attempts in parallel
      if (fixPromises.length > 0) {
        const fixResults = await Promise.all(fixPromises);
        const successfulFixes = fixResults.filter((result) => result.fixed);

        if (successfulFixes.length > 0) {
          Logger.log(
            "info",
            "Auto-fixed lesson type fields",
            {
              studentId,
              fixedCount: successfulFixes.length,
              totalAttempts: fixResults.length,
              fixedLessons: successfulFixes.map((r) => r.lessonId),
            },
            "LessonService"
          );
        }
      }
    }

    Logger.log(
      "info",
      "Successfully retrieved student lessons",
      {
        studentId,
        lessonCount: lessons.length,
        breakdown: {
          individual: lessons.filter((l) => l.source === "individual_query")
            .length,
          group: lessons.filter((l) => l.source === "group_query").length,
          fallback: lessons.filter((l) => l.source === "fallback_query").length,
          teacher: lessons.filter((l) => l.source === "teacher_query").length,
        },
        queryResults: {
          individualCount: individualSnapshot.size,
          groupCount: enrolledGroupSnapshot.size,
          fallbackCount: fallbackSnapshot.size,
          teacherCount: teacherSnapshot.size,
        },
      },
      "LessonService"
    );

    // Remove temporary debugging fields before returning
    return lessons.map((lesson) => {
      const {
        source,
        needsTypeFieldFix,
        possibleDataMappingIssue,
        ...cleanLesson
      } = lesson;
      // These variables are intentionally unused - they're destructured to remove from the return object
      void source;
      void needsTypeFieldFix;
      void possibleDataMappingIssue;
      return cleanLesson;
    });
  } catch (error) {
    Logger.log(
      "error",
      "Error getting student lessons",
      { error: error.message, studentId, stack: error.stack },
      "LessonService"
    );
    throw error;
  }
};

/**
 * Get teacher lessons
 * @param {string} teacherId - The teacher ID
 * @returns {Promise<Array>} - The teacher's lessons
 */
export const getTeacherLessons = async (teacherId) => {
  try {
    const q = query(
      collection(db, "lessons"),
      where("teacherId", "==", teacherId),
      orderBy("startTime", "desc")
    );

    const querySnapshot = await getDocs(q);
    const lessons = [];

    querySnapshot.forEach((doc) => {
      lessons.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    return lessons;
  } catch (error) {
    Logger.log(
      "error",
      "Error getting teacher lessons",
      { error: error.message, teacherId },
      "LessonService"
    );
    throw error;
  }
};

/**
 * Get reschedule requests for a user
 * @param {string} userId - The user ID
 * @param {string} userType - The user type ('student' or 'teacher')
 * @returns {Promise<Array>} - The reschedule requests
 */
export const getRescheduleRequests = async (userId, userType) => {
  try {
    // We need to fetch lessons where:
    // 1. The status is reschedule_requested
    // 2. The user is the recipient of the request (not the requester)

    let q;
    if (userType === "teacher") {
      // Student requested the reschedule
      q = query(
        collection(db, "lessons"),
        where("teacherId", "==", userId),
        where("status", "==", "reschedule_requested"),
        where("rescheduleRequestedBy", "!=", userId),
        orderBy("rescheduleRequestedBy") // Required for the inequality filter
        // Removed orderBy("startTime") to avoid composite index requirement
      );
    } else {
      // Teacher requested the reschedule
      q = query(
        collection(db, "lessons"),
        where("studentId", "==", userId),
        where("status", "==", "reschedule_requested"),
        where("rescheduleRequestedBy", "!=", userId),
        orderBy("rescheduleRequestedBy") // Required for the inequality filter
        // Removed orderBy("startTime") to avoid composite index requirement
      );
    }

    const querySnapshot = await getDocs(q);
    const requests = [];

    querySnapshot.forEach((doc) => {
      requests.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    // Sort by startTime (client-side sorting)
    requests.sort((a, b) => {
      const aTime = a.startTime?.toDate?.() || new Date(a.startTime);
      const bTime = b.startTime?.toDate?.() || new Date(b.startTime);
      return aTime - bTime; // Ascending order for reschedule requests
    });

    return requests;
  } catch (error) {
    Logger.log(
      "error",
      "Error getting reschedule requests",
      { error: error.message, userId, userType },
      "LessonService"
    );
    throw error;
  }
};

/**
 * Add a meeting link to a lesson
 * @param {string} lessonId - The lesson ID
 * @param {string} meetingLink - The meeting link URL
 * @param {string} addedBy - The user ID who added the link
 * @param {boolean} persistMeetingLink - Whether to persist the link setting for future lessons
 * @returns {Promise<boolean>} - Success status
 */
export const addMeetingLink = async (
  lessonId,
  meetingLink,
  addedBy,
  persistMeetingLink = false
) => {
  Logger.log(
    "info",
    "Adding meeting link to lesson",
    { lessonId, addedBy, persistMeetingLink },
    "LessonService"
  );

  try {
    if (!FeatureFlags.core.meetingLinks) {
      throw new Error("Meeting links feature is disabled");
    }

    // Validate the meeting link
    if (!meetingLink || typeof meetingLink !== "string") {
      throw new Error("Meeting link is required and must be a string");
    }

    if (!isValidURL(meetingLink)) {
      throw new Error("Meeting link must be a valid URL");
    }

    // Verify the lesson exists and user has permission
    const lesson = await getLessonById(lessonId);
    if (lesson.teacherId !== addedBy) {
      throw new Error("Only the lesson teacher can add meeting links");
    }

    // Only allow adding links to confirmed lessons
    if (lesson.status !== LESSON_STATUSES.CONFIRMED) {
      throw new Error("Meeting links can only be added to confirmed lessons");
    }

    await updateDoc(doc(db, "lessons", lessonId), {
      meetingLink,
      meetingLinkAddedAt: new Date(),
      meetingLinkAddedBy: addedBy,
      persistMeetingLink,
      updatedAt: new Date(),
    });

    Logger.log(
      "info",
      "Successfully added meeting link to lesson",
      { lessonId, addedBy },
      "LessonService"
    );

    // Send meeting link notification to student
    try {
      await NotificationService.sendNotification(
        lesson.studentId,
        NOTIFICATION_TYPES.MEETING_LINK_ADDED,
        {
          lessonId,
          lessonData: lesson,
          meetingLink,
          subject: lesson.subject || "Music Lesson",
          startTime: lesson.startTime,
          endTime: lesson.endTime,
          teacherName: lesson.teacherName || "Your Teacher",
          addedAt: new Date(),
        }
      );

      Logger.log(
        "info",
        "Successfully sent meeting link notification",
        { lessonId, addedBy },
        "LessonService"
      );
    } catch (notificationError) {
      // Don't fail the meeting link addition if notification fails
      Logger.log(
        "error",
        "Failed to send meeting link notification",
        {
          lessonId,
          addedBy,
          error: notificationError.message,
        },
        "LessonService"
      );
    }

    return true;
  } catch (error) {
    Logger.log(
      "error",
      "Error adding meeting link",
      { error: error.message, lessonId, addedBy },
      "LessonService"
    );
    throw error;
  }
};

/**
 * Update a meeting link for a lesson
 * @param {string} lessonId - The lesson ID
 * @param {string} meetingLink - The new meeting link URL
 * @param {string} updatedBy - The user ID who updated the link
 * @returns {Promise<boolean>} - Success status
 */
export const updateMeetingLink = async (lessonId, meetingLink, updatedBy) => {
  Logger.log(
    "info",
    "Updating meeting link for lesson",
    { lessonId, updatedBy },
    "LessonService"
  );

  try {
    if (!FeatureFlags.core.meetingLinks) {
      throw new Error("Meeting links feature is disabled");
    }

    // Validate the meeting link
    if (!meetingLink || typeof meetingLink !== "string") {
      throw new Error("Meeting link is required and must be a string");
    }

    if (!isValidURL(meetingLink)) {
      throw new Error("Meeting link must be a valid URL");
    }

    // Verify the lesson exists and user has permission
    const lesson = await getLessonById(lessonId);
    if (lesson.teacherId !== updatedBy) {
      throw new Error("Only the lesson teacher can update meeting links");
    }

    // Only allow updating links for confirmed lessons
    if (lesson.status !== LESSON_STATUSES.CONFIRMED) {
      throw new Error(
        "Meeting links can only be updated for confirmed lessons"
      );
    }

    await updateDoc(doc(db, "lessons", lessonId), {
      meetingLink,
      meetingLinkAddedAt: new Date(),
      meetingLinkAddedBy: updatedBy,
      updatedAt: new Date(),
    });

    Logger.log(
      "info",
      "Successfully updated meeting link for lesson",
      { lessonId, updatedBy },
      "LessonService"
    );

    return true;
  } catch (error) {
    Logger.log(
      "error",
      "Error updating meeting link",
      { error: error.message, lessonId, updatedBy },
      "LessonService"
    );
    throw error;
  }
};

/**
 * Remove a meeting link from a lesson
 * @param {string} lessonId - The lesson ID
 * @param {string} removedBy - The user ID who removed the link
 * @returns {Promise<boolean>} - Success status
 */
export const removeMeetingLink = async (lessonId, removedBy) => {
  Logger.log(
    "info",
    "Removing meeting link from lesson",
    { lessonId, removedBy },
    "LessonService"
  );

  try {
    if (!FeatureFlags.core.meetingLinks) {
      throw new Error("Meeting links feature is disabled");
    }

    // Verify the lesson exists and user has permission
    const lesson = await getLessonById(lessonId);
    if (lesson.teacherId !== removedBy) {
      throw new Error("Only the lesson teacher can remove meeting links");
    }

    await updateDoc(doc(db, "lessons", lessonId), {
      meetingLink: null,
      meetingLinkAddedAt: null,
      meetingLinkAddedBy: null,
      updatedAt: new Date(),
    });

    Logger.log(
      "info",
      "Successfully removed meeting link from lesson",
      { lessonId, removedBy },
      "LessonService"
    );

    return true;
  } catch (error) {
    Logger.log(
      "error",
      "Error removing meeting link",
      { error: error.message, lessonId, removedBy },
      "LessonService"
    );
    throw error;
  }
};

/**
 * Fix a lesson's type field if it's missing or incorrect
 * @param {string} lessonId - The lesson ID
 * @param {Object} lessonData - The current lesson data
 * @returns {Promise<boolean>} - Success status
 */
export const fixLessonTypeField = async (lessonId, lessonData) => {
  Logger.log(
    "info",
    "Attempting to fix lesson type field",
    {
      lessonId,
      currentType: lessonData.type,
      hasStudentId: !!lessonData.studentId,
      hasCurrentStudents: !!lessonData.currentStudents,
    },
    "LessonService"
  );

  try {
    // Determine the correct type based on lesson data
    let correctType = null;

    if (
      lessonData.currentStudents &&
      Array.isArray(lessonData.currentStudents)
    ) {
      // Has currentStudents array - likely group lesson
      correctType = "group";
    } else if (lessonData.studentId) {
      // Has studentId but no currentStudents array - likely individual lesson
      correctType = "individual";
    }

    if (!correctType) {
      Logger.log(
        "warn",
        "Could not determine correct type for lesson",
        { lessonId, lessonData: Object.keys(lessonData) },
        "LessonService"
      );
      return false;
    }

    // Only update if the type is actually wrong or missing
    if (lessonData.type !== correctType) {
      await updateDoc(doc(db, "lessons", lessonId), {
        type: correctType,
        updatedAt: new Date(),
        typeFieldFixed: true,
        typeFieldFixedAt: new Date(),
      });

      Logger.log(
        "info",
        "Successfully fixed lesson type field",
        { lessonId, oldType: lessonData.type, newType: correctType },
        "LessonService"
      );

      return true;
    }

    Logger.log(
      "debug",
      "Lesson type field is already correct",
      { lessonId, type: lessonData.type },
      "LessonService"
    );

    return false;
  } catch (error) {
    Logger.log(
      "error",
      "Error fixing lesson type field",
      { error: error.message, lessonId },
      "LessonService"
    );
    throw error;
  }
};

/**
 * Submit a review for a lesson
 * @param {Object} reviewData - The review data
 * @returns {Promise<string>} - The ID of the created review
 */
export const submitReview = async (reviewData) => {
  Logger.log(
    "info",
    "Submitting lesson review",
    {
      lessonId: reviewData.lessonId,
      teacherId: reviewData.teacherId,
      studentId: reviewData.studentId,
      rating: reviewData.rating,
      hasBadges: reviewData.badges?.length > 0,
    },
    "LessonService"
  );

  try {
    // Validate review data
    validateReview(reviewData);

    // Check if lesson exists and user has permission to review
    const lesson = await getLessonById(reviewData.lessonId);

    if (lesson.studentId !== reviewData.studentId) {
      throw new Error("Only the lesson student can submit a review");
    }

    if (lesson.teacherId !== reviewData.teacherId) {
      throw new Error("Review teacher ID does not match lesson teacher");
    }

    // Check if lesson is completed or confirmed
    if (
      lesson.status !== LESSON_STATUSES.COMPLETED &&
      lesson.status !== LESSON_STATUSES.CONFIRMED
    ) {
      throw new Error(
        "Reviews can only be submitted for completed or confirmed lessons"
      );
    }

    // Check if review already exists
    const existingReviewQuery = query(
      collection(db, COLLECTIONS.REVIEWS),
      where("lessonId", "==", reviewData.lessonId),
      where("studentId", "==", reviewData.studentId)
    );

    const existingReviews = await getDocs(existingReviewQuery);
    if (!existingReviews.empty) {
      throw new Error("A review has already been submitted for this lesson");
    }

    // Create the review
    const review = {
      ...reviewData,
      status: REVIEW_STATUS.ACTIVE,
      helpfulCount: 0,
      reportCount: 0,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    const reviewRef = await addDoc(collection(db, COLLECTIONS.REVIEWS), review);

    Logger.log(
      "info",
      "Successfully submitted review",
      {
        reviewId: reviewRef.id,
        lessonId: reviewData.lessonId,
        rating: reviewData.rating,
      },
      "LessonService"
    );

    // Award badges to the teacher based on the review
    if (reviewData.badges && reviewData.badges.length > 0) {
      try {
        await awardBadgesFromReview(
          reviewData.teacherId,
          reviewData.badges,
          reviewRef.id
        );

        Logger.log(
          "info",
          "Successfully awarded badges from review",
          {
            reviewId: reviewRef.id,
            teacherId: reviewData.teacherId,
            badgeCount: reviewData.badges.length,
          },
          "LessonService"
        );
      } catch (badgeError) {
        // Don't fail the review submission if badge awarding fails
        Logger.log(
          "error",
          "Failed to award badges from review",
          {
            reviewId: reviewRef.id,
            teacherId: reviewData.teacherId,
            error: badgeError.message,
          },
          "LessonService"
        );
      }
    }

    // Update lesson to mark it as reviewed
    try {
      await updateDoc(doc(db, "lessons", reviewData.lessonId), {
        hasReview: true,
        reviewId: reviewRef.id,
        reviewSubmittedAt: new Date(),
        updatedAt: new Date(),
      });

      Logger.log(
        "info",
        "Successfully updated lesson with review status",
        { lessonId: reviewData.lessonId, reviewId: reviewRef.id },
        "LessonService"
      );
    } catch (updateError) {
      // Log error but don't fail the review submission
      Logger.log(
        "error",
        "Failed to update lesson with review status",
        {
          lessonId: reviewData.lessonId,
          reviewId: reviewRef.id,
          error: updateError.message,
        },
        "LessonService"
      );
    }

    return reviewRef.id;
  } catch (error) {
    Logger.log(
      "error",
      "Error submitting review",
      {
        error: error.message,
        lessonId: reviewData.lessonId,
        studentId: reviewData.studentId,
      },
      "LessonService"
    );
    throw error;
  }
};

/**
 * Get reviews for a teacher
 * @param {string} teacherId - The teacher ID
 * @param {Object} options - Query options
 * @param {number} options.limit - Maximum number of reviews to return
 * @param {string} options.orderBy - Field to order by (default: 'createdAt')
 * @param {string} options.direction - Order direction ('asc' or 'desc', default: 'desc')
 * @returns {Promise<Array>} - The teacher's reviews
 */
export const getTeacherReviews = async (teacherId, options = {}) => {
  Logger.log(
    "info",
    "Fetching teacher reviews",
    { teacherId, options },
    "LessonService"
  );

  try {
    const {
      limit: limitCount = 50,
      orderBy: orderField = "createdAt",
      direction = "desc",
    } = options;

    let reviewQuery = query(
      collection(db, COLLECTIONS.REVIEWS),
      where("teacherId", "==", teacherId),
      where("status", "==", REVIEW_STATUS.ACTIVE)
    );

    // Add ordering
    reviewQuery = query(reviewQuery, orderBy(orderField, direction));

    // Add limit if specified
    if (limitCount) {
      reviewQuery = query(reviewQuery, limit(limitCount));
    }

    const querySnapshot = await getDocs(reviewQuery);
    const reviews = [];

    querySnapshot.forEach((doc) => {
      reviews.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    Logger.log(
      "info",
      "Successfully retrieved teacher reviews",
      { teacherId, reviewCount: reviews.length },
      "LessonService"
    );

    return reviews;
  } catch (error) {
    Logger.log(
      "error",
      "Error getting teacher reviews",
      { error: error.message, teacherId },
      "LessonService"
    );
    throw error;
  }
};

/**
 * Get review for a specific lesson
 * @param {string} lessonId - The lesson ID
 * @param {string} studentId - The student ID (for security check)
 * @returns {Promise<Object|null>} - The review or null if not found
 */
export const getLessonReview = async (lessonId, studentId) => {
  Logger.log(
    "info",
    "Fetching lesson review",
    { lessonId, studentId },
    "LessonService"
  );

  try {
    const reviewQuery = query(
      collection(db, COLLECTIONS.REVIEWS),
      where("lessonId", "==", lessonId),
      where("studentId", "==", studentId)
    );

    const querySnapshot = await getDocs(reviewQuery);

    if (querySnapshot.empty) {
      Logger.log(
        "info",
        "No review found for lesson",
        { lessonId, studentId },
        "LessonService"
      );
      return null;
    }

    const reviewDoc = querySnapshot.docs[0];
    const review = {
      id: reviewDoc.id,
      ...reviewDoc.data(),
    };

    Logger.log(
      "info",
      "Successfully retrieved lesson review",
      { lessonId, reviewId: review.id },
      "LessonService"
    );

    return review;
  } catch (error) {
    Logger.log(
      "error",
      "Error getting lesson review",
      { error: error.message, lessonId, studentId },
      "LessonService"
    );
    throw error;
  }
};

/**
 * Update an existing review
 * @param {string} reviewId - The review ID
 * @param {Object} updateData - The data to update
 * @param {string} studentId - The student ID (for security check)
 * @returns {Promise<boolean>} - Success status
 */
export const updateReview = async (reviewId, updateData, studentId) => {
  Logger.log(
    "info",
    "Updating review",
    { reviewId, studentId, updateFields: Object.keys(updateData) },
    "LessonService"
  );

  try {
    // Get existing review to verify ownership
    const reviewDoc = await getDoc(doc(db, COLLECTIONS.REVIEWS, reviewId));

    if (!reviewDoc.exists()) {
      throw new Error("Review not found");
    }

    const existingReview = reviewDoc.data();

    if (existingReview.studentId !== studentId) {
      throw new Error("Only the review author can update this review");
    }

    // Validate update data
    const mergedData = { ...existingReview, ...updateData };
    validateReview(mergedData);

    // Update the review
    await updateDoc(doc(db, COLLECTIONS.REVIEWS, reviewId), {
      ...updateData,
      updatedAt: new Date(),
    });

    Logger.log(
      "info",
      "Successfully updated review",
      { reviewId, studentId },
      "LessonService"
    );

    // If badges were updated, handle badge changes
    if (updateData.badges && updateData.badges !== existingReview.badges) {
      try {
        // Award new badges if any
        const newBadges = updateData.badges.filter(
          (badgeId) => !existingReview.badges.includes(badgeId)
        );

        if (newBadges.length > 0) {
          await awardBadgesFromReview(
            existingReview.teacherId,
            newBadges,
            reviewId
          );

          Logger.log(
            "info",
            "Successfully awarded new badges from review update",
            {
              reviewId,
              teacherId: existingReview.teacherId,
              newBadgeCount: newBadges.length,
            },
            "LessonService"
          );
        }
      } catch (badgeError) {
        // Log error but don't fail the review update
        Logger.log(
          "error",
          "Failed to handle badge changes in review update",
          {
            reviewId,
            error: badgeError.message,
          },
          "LessonService"
        );
      }
    }

    return true;
  } catch (error) {
    Logger.log(
      "error",
      "Error updating review",
      { error: error.message, reviewId, studentId },
      "LessonService"
    );
    throw error;
  }
};

/**
 * Mark a lesson as completed
 * @param {string} lessonId - The lesson ID
 * @param {string} completedBy - The user ID who marked it complete
 * @param {boolean} problemReported - Whether a problem was reported
 * @param {string} problemDetails - Details about any problems
 * @returns {Promise<boolean>} - Success status
 */
export const markLessonCompleted = async (
  lessonId,
  completedBy,
  problemReported = false,
  problemDetails = ""
) => {
  Logger.log(
    "info",
    "Marking lesson as completed",
    {
      lessonId,
      completedBy,
      problemReported,
      hasProblemDetails: !!problemDetails,
    },
    "LessonService"
  );

  try {
    // Verify the lesson exists and user has permission
    const lesson = await getLessonById(lessonId);

    if (lesson.teacherId !== completedBy && lesson.studentId !== completedBy) {
      throw new Error("Only lesson participants can mark it as completed");
    }

    // Only allow marking confirmed lessons as completed
    if (lesson.status !== LESSON_STATUSES.CONFIRMED) {
      throw new Error("Only confirmed lessons can be marked as completed");
    }

    // Call Cloud Function to handle completion workflow if available
    try {
      const result = await completeLessonCloudFunction({
        lessonId,
        completedBy,
        problemReported,
        problemDetails,
      });

      Logger.log(
        "info",
        "Cloud function completeLesson completed",
        { lessonId, completedBy, result: result.data },
        "LessonService"
      );
    } catch (cloudError) {
      // If Cloud Function doesn't exist, handle locally
      Logger.log(
        "warn",
        "Cloud function not available, handling completion locally",
        { lessonId, cloudError: cloudError.message },
        "LessonService"
      );

      // Update lesson status to completed following security rules structure
      const completionTimestamp = Timestamp.now();
      const currentStatusHistory = lesson.statusHistory || [];

      // Prepare completion details matching Cloud Function structure
      const completionDetails = {
        completedAt: completionTimestamp,
        completedBy: completedBy,
        problemReported: problemReported,
      };

      if (problemReported && problemDetails) {
        completionDetails.problemDetails = problemDetails;
        completionDetails.problemReportedAt = completionTimestamp;
      }

      // Determine new status based on problem reporting (matching Cloud Function logic)
      const newStatus = problemReported
        ? LESSON_STATUSES.CANCELLATION_REQUESTED
        : LESSON_STATUSES.COMPLETED;

      // Prepare status history entry - MUST match security rules exactly
      // Security rules expect ONLY ['status', 'timestamp', 'changedBy'] keys
      const statusHistoryEntry = {
        status: newStatus,
        timestamp: completionTimestamp,
        changedBy: completedBy,
      };

      const updateData = {
        status: newStatus,
        completionDetails: completionDetails,
        updatedAt: completionTimestamp,
        statusHistory: [...currentStatusHistory, statusHistoryEntry],
      };

      // Add review prompt tracking for successful completion (matching Cloud Function)
      if (!problemReported) {
        updateData.reviewPromptScheduled = true;
        updateData.reviewPromptScheduledAt = completionTimestamp;
      }

      Logger.log(
        "debug",
        "Local fallback - preparing lesson completion update",
        {
          lessonId,
          newStatus,
          problemReported,
          completedBy,
          statusHistoryLength: updateData.statusHistory.length,
        },
        "LessonService"
      );

      await updateDoc(doc(db, "lessons", lessonId), updateData);

      // Schedule review prompt notification (24 hours after completion) if no problems
      if (!problemReported) {
        try {
          const reviewPromptTime = new Date(Date.now() + 24 * 60 * 60 * 1000); // 24 hours from now

          await NotificationService.sendNotification(
            lesson.studentId,
            NOTIFICATION_TYPES.LESSON_REVIEW_PROMPT,
            {
              lessonId,
              lessonData: lesson,
              teacherId: lesson.teacherId,
              teacherName: lesson.teacherName || "Your Teacher",
              subject: lesson.subject || "Music Lesson",
              completedAt: new Date(),
            },
            "immediate", // Send immediately, but can be configured
            reviewPromptTime // Schedule for later
          );

          Logger.log(
            "info",
            "Successfully scheduled review prompt notification",
            { lessonId, scheduledFor: reviewPromptTime },
            "LessonService"
          );
        } catch (notificationError) {
          // Don't fail the lesson completion if notification scheduling fails
          Logger.log(
            "error",
            "Failed to schedule review prompt notification",
            {
              lessonId,
              error: notificationError.message,
            },
            "LessonService"
          );
        }
      }

      // Send completion notification to the other party
      try {
        const recipientId =
          completedBy === lesson.teacherId
            ? lesson.studentId
            : lesson.teacherId;
        // const recipientName = completedBy === lesson.teacherId ? lesson.studentName : lesson.teacherName;
        const completedByRole =
          completedBy === lesson.teacherId ? "teacher" : "student";

        await NotificationService.sendNotification(
          recipientId,
          NOTIFICATION_TYPES.LESSON_COMPLETED,
          {
            lessonId,
            lessonData: lesson,
            subject: lesson.subject || "Music Lesson",
            startTime: lesson.startTime,
            endTime: lesson.endTime,
            completedByRole,
            completedAt: new Date(),
            problemReported,
            problemDetails: problemReported ? problemDetails : undefined,
          }
        );

        Logger.log(
          "info",
          "Successfully sent lesson completion notification",
          { lessonId, completedBy, recipientId },
          "LessonService"
        );
      } catch (notificationError) {
        Logger.log(
          "error",
          "Failed to send lesson completion notification",
          { lessonId, error: notificationError.message },
          "LessonService"
        );
      }
    }

    Logger.log(
      "info",
      "Successfully marked lesson as completed",
      { lessonId, completedBy, problemReported },
      "LessonService"
    );

    return true;
  } catch (error) {
    Logger.log(
      "error",
      "Error marking lesson as completed",
      { error: error.message, lessonId, completedBy },
      "LessonService"
    );
    throw error;
  }
};

/**
 * Get booked time slots for a teacher on a specific date
 * Used for conflict detection when generating available time slots
 * @param {string} teacherId - The teacher's ID
 * @param {Date} targetDate - The date to check bookings for
 * @returns {Promise<Array>} Array of booked time ranges [{ startTime: Date, endTime: Date }]
 */
export const getTeacherBookedSlots = async (teacherId, targetDate) => {
  Logger.log(
    "info",
    "Fetching booked slots for teacher",
    { teacherId, targetDate },
    "LessonService"
  );

  try {
    // Call Cloud Function to get booked slots (bypasses security rules)
    const getBookedSlotsFunc = httpsCallable(
      functions,
      "getTeacherBookedSlots"
    );
    const result = await getBookedSlotsFunc({
      teacherId,
      targetDate: targetDate.toISOString(),
    });

    // Convert ISO strings back to Date objects
    const bookedSlots = result.data.bookedSlots.map((slot) => ({
      startTime: new Date(slot.startTime),
      endTime: new Date(slot.endTime),
    }));

    Logger.log(
      "info",
      "Successfully retrieved booked slots",
      {
        teacherId,
        targetDate,
        slotCount: bookedSlots.length,
      },
      "LessonService"
    );

    return bookedSlots;
  } catch (error) {
    Logger.log(
      "error",
      "Error getting booked slots",
      {
        error: error.message,
        teacherId,
        targetDate,
      },
      "LessonService"
    );
    throw error;
  }
};
